import React, { useEffect, useMemo, useState } from 'react';
import { MdRefresh, MdHistory } from 'react-icons/md';
import { tradingService } from '../../services/tradingService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Operator audit trail — who did what, when, on ONE account. Feeds the External-Book
// "Audit" tab. Read-only; the account list + the audit rows are BOTH scoped server-side
// to the caller's accessible accounts (an operator sees only their delegated account).
// See apps/trading/services/operation_audit.py + Plans/external_book_operator_access_and_audit.md.

const ACTION_TONE = {
  order_download: 'text-gray-500 dark:text-gray-400',
  order_email: 'text-blue-500',
  fills_upload: 'text-emerald-500',
  no_fills_confirm: 'text-gray-500 dark:text-gray-400',
  signal_regenerate: 'text-brand-gold',
  infusion_add: 'text-emerald-500',
  infusion_edit: 'text-amber-500',
  infusion_delete: 'text-red-500',
};

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Compact one-line summary of the JSON meta blob (recipients, counts, amount, etc.).
function fmtMeta(meta) {
  if (!meta || typeof meta !== 'object') return '';
  const parts = [];
  if (meta.amount != null) parts.push(`$${Number(meta.amount).toLocaleString()}`);
  if (meta.recipients?.length) parts.push(`→ ${meta.recipients.join(', ')}`);
  if (meta.legs != null) parts.push(`${meta.legs} legs`);
  if (meta.applied != null) parts.push(`${meta.applied} applied`);
  if (meta.parsed != null && meta.applied == null) parts.push(`${meta.parsed} parsed`);
  if (meta.orders_affected != null) parts.push(`${meta.orders_affected} orders`);
  if (meta.format) parts.push(String(meta.format).toUpperCase());
  if (meta.kind) parts.push(meta.kind);
  if (meta.regenerated_now) parts.push('regen dispatched');
  else if (meta.decision === 'at_evening') parts.push('evening run builds the sheet');
  if (meta.date && meta.amount != null) parts.push(`on ${meta.date}`);
  return parts.join(' · ');
}

const AUDIT_COLS = [
  { key: 'occurred_at', type: 'string', get: (r) => r.occurred_at || '' },
  { key: 'actor', type: 'string', get: (r) => r.actor || '' },
  { key: 'action', type: 'string', get: (r) => r.action_label || r.action || '' },
  { key: 'target_ref', type: 'string', get: (r) => r.target_ref || '' },
];

const AuditPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Accessible accounts (server-scoped) — an operator gets exactly their delegated account.
  useEffect(() => {
    tradingService.listIBKRAccounts()
      .then((r) => {
        const list = (r.data?.results || r.data || []).filter((a) => a.is_model);
        setAccounts(list);
        if (list.length && accountId == null) setAccountId(list[0].id);
      })
      .catch(() => setError('Could not load accounts.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = (id) => {
    if (id == null) return;
    setLoading(true);
    setError('');
    tradingService.getModelAudit(id, 300)
      .then((r) => setEvents(r.data?.events || []))
      .catch(() => setError('Could not load the audit trail.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(accountId); /* eslint-disable-next-line */ }, [accountId]);

  const { sorted, toggle, indicator } = useSortableRows(events, AUDIT_COLS, { key: 'occurred_at', dir: 'desc' });
  const activeAccount = useMemo(
    () => accounts.find((a) => a.id === accountId),
    [accounts, accountId],
  );

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <MdHistory className="text-brand-gold text-xl" />
            <h2 className="font-serif text-lg">Operation Audit</h2>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide">
            Who did what, when — order sheets, fills, infusions, regenerations
          </p>
        </div>
        <div className="flex items-center gap-2">
          {accounts.length > 1 && (
            <select
              value={accountId ?? ''}
              onChange={(e) => setAccountId(Number(e.target.value))}
              className="text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-gray-700 dark:text-gray-200"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => load(accountId)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-brand-gold hover:border-brand-gold/40 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <MdRefresh className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {activeAccount && (
        <div className="text-[11px] uppercase tracking-[2px] text-gray-400 dark:text-gray-600 mb-2">
          {activeAccount.name}
        </div>
      )}

      {error && (
        <div className="text-sm text-red-500 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="overflow-x-auto scrollbar-thin rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="w-full text-[13px] tabular-nums">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800 [&>th]:px-3 [&>th]:py-2.5">
              <SortableTh label="When" colKey="occurred_at" toggle={toggle} indicator={indicator} align="left" />
              <SortableTh label="Actor" colKey="actor" toggle={toggle} indicator={indicator} align="left" />
              <SortableTh label="Action" colKey="action" toggle={toggle} indicator={indicator} align="left" />
              <SortableTh label="Target" colKey="target_ref" toggle={toggle} indicator={indicator} align="left" />
              <th className="text-left px-3 py-2.5">Detail</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e) => (
              <tr
                key={e.id}
                className="border-b border-gray-100 dark:border-gray-800/60 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/30 [&>td]:px-3 [&>td]:py-2"
              >
                <td className="text-left whitespace-nowrap text-gray-600 dark:text-gray-300">{fmtWhen(e.occurred_at)}</td>
                <td className="text-left font-medium text-gray-800 dark:text-gray-100">{e.actor || '—'}</td>
                <td className={`text-left font-medium ${ACTION_TONE[e.action] || 'text-gray-600 dark:text-gray-300'}`}>
                  {e.action_label || e.action}
                </td>
                <td className="text-left text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={e.target_ref}>
                  {e.target_ref || '—'}
                </td>
                <td className="text-left text-gray-500 dark:text-gray-400">{fmtMeta(e.meta)}</td>
              </tr>
            ))}
            {!loading && sorted.length === 0 && !error && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                  No operator actions recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditPage;
